import React, { FunctionComponent } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import styled from "styled-components";
import { IRootReducer } from "../../../Models/IRootReducer";
import { IProfileRecord, ISegment } from "../../../Models/Models";
import { segmentFormatter } from "../../../Util/ReportUtil";

interface IRechartPyramid {
    chartHeight?: number;
    selection1: IProfileRecord[];
    selection2?: IProfileRecord[];
    equalYMax?: boolean;
}
interface IChartContainer {
    chartHeight?: number;
    width: number;
}
interface IRechartPyramidStore {
    segments: ISegment[];
}

const ChartContainer = styled("div")`
  height: ${(props: IChartContainer) =>
        props.chartHeight ? props.chartHeight : 360}px;
  width: ${(props: IChartContainer) => props.width}%;
`;

const colors = { P: "#ff2b2b", S: "#5833ff", N: "#a8a095", T: "#a8a095" };

const formatYValue = (num: number): string => {
    return `${Math.round(Number(num) * 10000) / 100}%`;
};

export const RechartPyramid: FunctionComponent<IRechartPyramid> = (
    props: IRechartPyramid
) => {
    /** store */
    const store = useSelector((root: IRootReducer): IRechartPyramidStore => {
        return {
            segments: root.matchReportOutput.mosaicSegments,
        };
    }, shallowEqual);

    let maxValue: number;
    if (props.selection2 && props.equalYMax) {
        const sel1Max = Math.max(...props.selection1.map((sel) => sel.pct));
        const sel2Max = Math.max(...props.selection2.map((sel) => sel.pct));

        maxValue = sel1Max >= sel2Max ? sel1Max : sel2Max;
        maxValue += 0.05 * maxValue; // give it padding based on the max value, not a set value
    }

    const formatTooltipText = (value: number, name: string): [string, string] => {
        const val = formatYValue(value);
        return [val, "Percent"];
    };

    const SingleChart = (chartData: IProfileRecord[], side: "left" | "right") => {
        return (
            <ChartContainer
                chartHeight={props.chartHeight}
                width={props.selection1.length > 0 && props.selection2 ? 50 : 100}
            >
                <ResponsiveContainer>
                    <ComposedChart
                        data={chartData}
                        barSize={50}
                        barCategoryGap={"5%"}
                        layout="vertical"
                        syncId="PyramidChart"
                        baseValue="dataMax"
                    >
                        <CartesianGrid vertical={true} horizontal={true} />
                        <XAxis
                            tickFormatter={formatYValue}
                            dataKey="pct"
                            type="number"
                            reversed={side === "left" ? true : false}
                            orientation="top"
                            domain={props.equalYMax ? [0, maxValue] : [0, "auto"]}
                        />
                        <YAxis
                            dataKey="seg"
                            orientation={side === "left" ? "right" : "left"}
                            type="category"
                            tickFormatter={side === "right" ? null : () => ""}
                            tickLine={true}
                            width={side === "left" ? 5 : 33}
                            tickMargin={side === "left" ? 0 : 5}
                        />
                        <Tooltip
                            formatter={formatTooltipText}
                            labelFormatter={(seg) =>
                                segmentFormatter(seg.toString(), store.segments)
                            }
                        />
                        <Bar dataKey="pct" barSize={20} maxBarSize={50}>
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.bCore ? colors[entry.bCore] : colors[entry.core]}
                                />
                            ))}
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
            </ChartContainer>
        );
    };

    return (
        <div style={{ display: "flex" }}>
            {props.selection1.length > 0
                && SingleChart(props.selection1, props.selection2 ? "left" : "right")}
            {props.selection2 && SingleChart(props.selection2, "right")}
        </div>
    );
};
